import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { MarketCard } from '@eika/card';

import { fetchApplicationById, fetchOngoingApplications } from '../../actions';
import { getAssetByName } from '../../assets';
import Loader from '../../components/Loader/Loader';
import { CustomerCaseInfoLandingPage } from '../../generated-types';
import { AppState } from '../../reducers';
import { CustomerCaseInfoLandingPageUpdate } from '../../reducers/ongoingapplications';
import { getProductIcon, getProductName } from '../../util/mappings';
import { TrackingStep, trackEvent } from '../../util/piwik-utils';
import LoanSearchForm from './LoanSearchForm';
import LoanUpdateForm from './LoanUpdateForm';

const LoanUpgrade = (): JSX.Element => {
    const dispatch = useDispatch<any>();
    const { appId } = useParams<'appId'>();
    const { vehicle, context, ongoingApplications, feature, loader, completion, calculator } = useSelector(
        (state: AppState) => state,
    );
    const { data } = context.config;
    const { text } = data;
    const { landingPageApplicationInfo } = ongoingApplications;

    const { landingPageTitle, landingPageNoOngoingApplications } = text;
    const { registrationNumber } = vehicle.vehicleDetails;
    const { salgspantActivateLoanFromLc } = feature;
    const { hasFetchedOngoingApplications, isFetchingApplication, fetchApplicationFailed } = loader;
    const loadedApplicationId = completion.ongoing.applicationId;
    const applicationDetailsLoaded =
        loadedApplicationId === appId && calculator.loanInfo.loanAmountWithoutFees !== undefined;

    let title = '';
    let vehicleEuroTaxDataFetched = false;
    let applicationId = '';
    let loanProduct = '';
    let loanType = '';

    useEffect(() => {
        trackEvent(TrackingStep.EuroTax);
        if (!hasFetchedOngoingApplications) dispatch(fetchOngoingApplications());
    }, [dispatch, hasFetchedOngoingApplications]);

    useEffect(() => {
        if (appId && !applicationDetailsLoaded && !isFetchingApplication && !fetchApplicationFailed) {
            dispatch(fetchApplicationById(appId));
        }
    }, [appId, applicationDetailsLoaded, dispatch, fetchApplicationFailed, isFetchingApplication]);

    let selectedApp: CustomerCaseInfoLandingPageUpdate | undefined;

    const getNoAppsMessage = () => {
        return (
            <div className="ongoing-applications ongoing-applications--none">
                <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                    <>
                        <h1>{landingPageTitle}</h1>
                        <p>{landingPageNoOngoingApplications}</p>
                    </>
                </div>
            </div>
        );
    };

    if (landingPageApplicationInfo && landingPageApplicationInfo.length) {
        if (appId) {
            selectedApp = landingPageApplicationInfo.find((apps: CustomerCaseInfoLandingPage) => {
                return apps.applicationId === appId;
            }) as CustomerCaseInfoLandingPageUpdate;
            if (selectedApp) {
                ({ loanProduct, loanType, applicationId } = selectedApp);
                title = getProductName(loanType, loanProduct);
                vehicleEuroTaxDataFetched = !!selectedApp.vehicleEuroTaxDataFetched;
            } else {
                return getNoAppsMessage();
            }
        } else {
            return getNoAppsMessage();
        }
    }

    const searchMode = !registrationNumber && !vehicleEuroTaxDataFetched;
    const key = `loaneurotax_${applicationId}`;

    if (loader.isFetchingOngoingApplications) {
        return (
            <div className="vehicle-info eurotax-search-form">
                <Loader title={text.loadingDefault} />
            </div>
        );
    }

    if (!salgspantActivateLoanFromLc) {
        return getNoAppsMessage();
    }

    return (
        <>
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                <MarketCard
                    key={key}
                    className="medium-centered-container eurotax-container"
                    title={title}
                    header={
                        <MarketCard.IllustrationHeader
                            image={{
                                src: getAssetByName(getProductIcon(loanType, loanProduct)),
                                alt: title,
                            }}
                        />
                    }
                >
                    {searchMode && selectedApp && <LoanSearchForm application={selectedApp} />}
                    {!searchMode && selectedApp && <LoanUpdateForm application={selectedApp} />}
                </MarketCard>
            </div>
        </>
    );
};
export default LoanUpgrade;
